import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import BreweriesFaq from '../faqs/BreweriesFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import CDN_BASE from '../../../utils/cdn';

const featuredTrucks = { featured: true };

function Breweries() {
  const config = useContext(ConfigContext);
  const {
    vehicle_type, city_name, site_title,
  } = config;
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  // PS3b's cost card keeps one button ("Start a recurring request"); the
  // "Ask about hosted service" mailto and the price table were removed by
  // owner decision on 7 Sep 2026.

  // CMS-driven copy. See Residential.jsx for the pattern. Seattle has no row
  // → falls through to hardcoded JSX. Austin has bespoke copy → renders CMS.
  const { content: cms } = usePageContent('services_breweries');

  return (
    <div>
      <Meta
        title={cms?.meta?.title || `Food ${vehicleType}s for breweries and taprooms in ${city_name}`}
        breadcrumbName='Breweries'
        type='website'
        description={cms?.meta?.description || `Bring rotating food ${vehicleType}s to your brewery or taproom in ${city_name}. Food ${vehicleType} schedules that pair great food with great beer.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-brewery-food-truck-services.webp`} alt='Food truck parked at a brewery taproom' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType}s for your brewery or taproom`}</h1>
            <p>{cms?.hero?.subheadline || `Set up a one-time visit or a recurring rotation. We build the schedule and coordinate with the ${vehicleType}s.`}</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.hero?.cta_label || `Set up food ${vehicleType} service`}
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList breweries title='Breweries we work with' />
      </div>

      <section className='Section'>
        <h2>{cms?.how_it_works?.heading || 'How it works'}</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-scheduled-food-truck-service.webp`}
            isResponsive
            alt='Food truck schedule for a brewery'
            title={cms?.how_it_works?.card1_title || 'We build the rotation'}
            text={cms?.how_it_works?.card1_body || `Choose the days and service window. We coordinate the ${vehicleType}s and keep the schedule current.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-food-truck-menu-email.webp`}
            isResponsive
            alt='Weekly menu email for brewery customers'
            title={cms?.how_it_works?.card2_title || 'Your customers know what’s coming'}
            text={cms?.how_it_works?.card2_body || `Share one page with upcoming ${vehicleType}s and menus.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-mobile-food-truck-service.webp`}
            isResponsive
            alt='Food truck serving brewery customers'
            title={cms?.how_it_works?.card3_title || 'One point of contact'}
            text={cms?.how_it_works?.card3_body || `Questions, schedule changes, and vendor coordination come through ${site_title}, not your taproom manager.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Featured food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>{cms?.benefits?.heading || 'What to expect'}</h2>
        {/* Fallback cards answer the bible §22 checklist; the CMS keys keep their legacy names. */}
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-employee-food-truck-perk.webp`}
            isResponsive
            alt='Food truck parked at a brewery'
            title={cms?.benefits?.attract_title || 'What we need from you'}
            text={cms?.benefits?.attract_body || `A place for the ${vehicleType} to park, a service window, and basic site-access details. We’ll work through the rest with you.`}
          />
          {/* Pricing per the client (4 Sep 2026); cancellations per Master Policies v1.0 §11. */}
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-workplace-food-truck.webp`}
            isResponsive
            alt='Customers ordering at a food truck'
            title={cms?.benefits?.staff_title || 'What it costs'}
            text={cms?.benefits?.staff_body || `Most taprooms run this themselves: submit a recurring request through the catering form, pick your own ${vehicleType}s, and there’s no program fee. Want us to run it instead? Hosted service is a monthly program fee we quote up front.`}
          >
            {/* PS3b. Recurrence is the Repeats mode on the wizard's date
                step; ?mode=repeats opens the form on that step with Repeats
                selected (backlog E3; design CR 70 decides whether the
                parameter or a plain link is drawn). */}
            <div className={tripleStyles.TripleActions}>
              <Link to='/catering/requests/new?mode=repeats' className='Button'>Start a recurring request</Link>
            </div>
          </Triple>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-team-food-truck-lunch.webp`}
            isResponsive
            alt='Customers eating food truck food at a brewery'
            title={cms?.benefits?.kitchen_title || `If a ${vehicleType} cancels`}
            text={cms?.benefits?.kitchen_body || `We look for another ${vehicleType} first. A ${vehicleType} that cancels late pays a fee, and your schedule updates the moment anything changes.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-local-food-truck-vendors.webp`}
            isResponsive
            alt='Local food truck vendor at a brewery'
            title={cms?.benefits?.local_title || 'One visit or a standing rotation'}
            text={cms?.benefits?.local_body || 'Try a single visit first, or set a weekly rotation from the start.'}
          />
        </div>
      </section>


      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-brewery-catering-event.webp`} alt='Customer credit packages for brewery patrons' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.credits?.heading || 'Meal credits for your customers'}</h3>
            <CmsRichText
              html={cms?.credits?.body}
              fallback={<p>Want to run a promotion or thank your regulars? We can set up credit packages so the {vehicleType}s become a perk with a dollar value attached.</p>}
            />
            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.credits?.cta_label || 'Ask about credits'}
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What people are saying' />

      <section className='Section--extraPadding'>
        <BreweriesFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>{cms?.form?.heading || 'Tell us about your brewery'}</h2>
            <ServicesForm formType='breweries' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default Breweries;
